import { create } from "zustand";

const INTAKE_STORAGE_KEY = "entry_intake";

export type EntryIntakeProps = {
  firstName: string;
  lastName: string;
  preferredName: string;
  email: string;
  promotionCode: string;
  isInvestTogether: boolean;
};

type EntryIntakeStore = {
  intake: EntryIntakeProps;
  setIntake: (props: Partial<EntryIntakeProps>) => void;
  clearIntake: () => void;
};

const EMPTY_INTAKE: EntryIntakeProps = {
  firstName: "",
  lastName: "",
  preferredName: "",
  email: "",
  promotionCode: "",
  isInvestTogether: false,
};

const readPersistedIntake = (): EntryIntakeProps => {
  try {
    const raw = localStorage.getItem(INTAKE_STORAGE_KEY);
    if (!raw) return EMPTY_INTAKE;
    const parsed = JSON.parse(raw) as Partial<EntryIntakeProps>;
    return {
      firstName: typeof parsed.firstName === "string" ? parsed.firstName : "",
      lastName: typeof parsed.lastName === "string" ? parsed.lastName : "",
      preferredName: typeof parsed.preferredName === "string" ? parsed.preferredName : "",
      email: typeof parsed.email === "string" ? parsed.email : "",
      promotionCode: typeof parsed.promotionCode === "string" ? parsed.promotionCode : "",
      isInvestTogether: typeof parsed.isInvestTogether === "boolean" ? parsed.isInvestTogether : false,
    };
  } catch {
    return EMPTY_INTAKE;
  }
};

const persistIntake = (intake: EntryIntakeProps) => {
  try {
    localStorage.setItem(INTAKE_STORAGE_KEY, JSON.stringify(intake));
  } catch {
    // Storage unavailable (private mode/quota) – flow still works in-memory.
  }
};

/**
 * Pre-auth intake collected during the get-started journey (name, email),
 * mirroring the mobile onboardingIntakeStore. Persisted so a refresh or an
 * abandoned session picks up right where the user left off.
 */
export const useEntryIntakeStore = create<EntryIntakeStore>((set, get) => ({
  intake: readPersistedIntake(),
  setIntake: (props) => {
    const intake = { ...get().intake, ...props };
    persistIntake(intake);
    set({ intake });
  },
  clearIntake: () => {
    try {
      localStorage.removeItem(INTAKE_STORAGE_KEY);
    } catch {
      // ignore
    }
    set({ intake: EMPTY_INTAKE });
  },
}));
